
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Review {
  id: string;
  user_id: string | null;
  provider_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export const useReviews = (providerId?: string) => {
  return useQuery({
    queryKey: ['reviews', providerId],
    queryFn: async () => {
      if (!providerId) return [];
      
      console.log('Fetching reviews for provider:', providerId);
      
      // First get the reviews
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });

      if (reviewsError) {
        console.error('Error fetching reviews:', reviewsError);
        throw reviewsError;
      }

      console.log('Reviews fetched:', reviewsData);

      // If no reviews, return empty array
      if (!reviewsData || reviewsData.length === 0) {
        return [];
      }

      // Get user IDs from reviews
      const userIds = reviewsData
        .map(review => review.user_id)
        .filter(Boolean) as string[];

      // If no user IDs, return reviews without profile data
      if (userIds.length === 0) {
        return reviewsData.map(review => ({
          ...review,
          profiles: null
        })) as Review[];
      }

      // Fetch profiles for the user IDs
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        // Return reviews without profile data if profiles fetch fails
        return reviewsData.map(review => ({
          ...review,
          profiles: null
        })) as Review[];
      }

      console.log('Profiles fetched:', profilesData);

      // Combine reviews with profiles
      const reviewsWithProfiles = reviewsData.map(review => {
        const profile = profilesData?.find(p => p.id === review.user_id);
        return {
          ...review,
          profiles: profile ? {
            first_name: profile.first_name,
            last_name: profile.last_name
          } : null
        };
      });

      return reviewsWithProfiles as Review[];
    },
    enabled: !!providerId,
    staleTime: 0, // Always refetch to get latest reviews
  });
};

export const useReviewCount = (providerId?: string) => {
  return useQuery({
    queryKey: ['review-count', providerId],
    queryFn: async () => {
      if (!providerId) return 0;
      
      const { count, error } = await supabase
        .from('reviews')
        .select('*', { count: 'exact', head: true })
        .eq('provider_id', providerId);

      if (error) {
        console.error('Error fetching review count:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !!providerId,
    staleTime: 60000, // Cache for 1 minute
  });
};
